<template>
    <div>
        <div
            class="video-player-playlist-item"
            :class="{ 'show-player': isTransitioning || activeVideoIndex === videoIndex }"
            v-for="(video, videoIndex) of activeVideos"
            :key="'video_item_' + videoIndex"
        >
            <VideoPlayer
                :max-volume="maxVolume"
                :max-duration="maxDuration"
                :set-duration="playlist.length > 1 ? 0 : setDuration"
                :playing="playing"
                :muted="activeVideoIndex !== videoIndex"
                :src="video.src"
                :controls="controls"
                :fade-time="fadeTime"
                :can-fade="true"
                :autoplay="localAutoplay"
                :style="{ transition: fadeTime + 'ms linear opacity', width: '100%' }"
                :loop="loop"
                ref="videoPlayers"
                @durationUpdated="videoDurationUpdated(video, $event)"
                @finish="videoFinished(video)"
            />

        </div>
    </div>
</template>

<script>
    import FadeMixin from '../mixins/FadeMixin';
    import VideoPlayer from './VideoPlayer.vue';

    export default {

        components: {
            VideoPlayer
        },

        mixins: [
            FadeMixin
        ],

        props: {
            src: {
                type: String
            },
            playlist: {
                type: Array
            },
            autoplay: {
                type: Boolean,
                default: false
            },
            playing: {
                type: Boolean,
                default: true
            },
            controls: {
                type: Boolean,
                default: true
            },
            setDuration: {
                type: Number,
                required: false,
            },

            maxDuration: {
                required: false,
                default: 0
            },

            loop: {
                type: Boolean,
                default: false
            }
        },

        computed: {
            activeVideo() {
                return this.activeVideos[this.activeVideoIndex] || null;
            }
        },

        watch: {
            playlist() {
                this.activeVideoIndex = 0;
                this.playlistItem = 0;
                this.activeVideos = this.buildActiveVideos(this.playlist, 0);
                this.clearTransitionTimer();
                this.clearFadeInTransitionTimer();
            },

            autoplay() {
                this.localAutoplay = this.autoplay;
            },

            setDuration() {
                // // No player or duration available
                // if (this.setDuration === null) {
                //     return;
                // }
                //
                // const playlistItem = getPlaylistItemForDuration(this.playlist, this.maxDuration, this.setDuration);
                //
                // this.playlistItem = this.playlist.indexOf(playlistItem.playlistItem);
                // this.buildActiveVideos(this.playlist, this.playlistItem);
                //
                // if (this.$refs.videoPlayers.length) {
                //     this.$refs.videoPlayers[0].changeFadeClass('in');
                // }
                // if (this.$refs.videoPlayers.length > 1) {
                //     this.$refs.videoPlayers[1].changeFadeClass('out');
                // }
                //
                // this.$refs.videoPlayers[0].setCurrentTime(playlistItem.offset);
                // this.$refs.videoPlayers[0].playPlayer();
                //
                // this.activeVideoIndex = 0;
                // this.clearFadeInTransitionTimer();
                // this.clearTransitionTimer();
                // this.isTransitioning = false;
            }
        },

        data() {
            return {
                playlistItem: 0,
                activeVideoIndex: 0,
                activeVideos: this.buildActiveVideos(this.playlist, 0),
                isTransitioning: false,
                transitionTimerId: null,
                fadeInTransitionTimerId: null,
                localAutoplay: this.autoplay
            };
        },

        methods: {
            buildActiveVideos(videos, offset = 0) {
                if (videos.length <= 1) {
                    return videos.map(video => {
                        return {
                            ...video,
                            src: {
                                src: video.src
                            }
                        };
                    });
                }

                // only show nearly active videos (for preloading)
                return [
                    videos.length >= offset ? videos[offset] : null,
                    videos.length >= offset + 1 ? videos[offset + 1] : null
                ].filter(Boolean).map(video => {
                    return {
                        ...video,
                        src: {
                            src: video.src
                        }
                    };
                });
            },

            getNextVideo() {
                return this.playlist[this.playlistItem + 1];
            },

            getCurrentVolume() {
                if (!this.$refs.videoPlayers || !this.$refs.videoPlayers[this.activeVideoIndex]) {
                    return null;
                }

                return this.$refs.videoPlayers[this.activeVideoIndex].getCurrentVolume();
            },

            videoDurationUpdated(video, duration) {
                if (video !== this.activeVideo || this.playlist.length <= 1) {
                    return;
                }

                const fadeTimeInSeconds = this.fadeTime / 1000;
                const totalDuration = video.duration || 0;
                const toPlay = totalDuration - duration - fadeTimeInSeconds;

                // Preloading happens seconds before the next video will start
                if (toPlay > 4) {
                    return;
                }

                const nextVideo = this.getNextVideo();

                if (!nextVideo) {
                    if (toPlay <= Math.max(fadeTimeInSeconds, 1)) {
                        if (!this.isTransitioning) {
                            this.isTransitioning = true;

                            if (this.fadeTime && this.$refs.videoPlayers[this.activeVideoIndex]) {
                                this.$refs.videoPlayers[this.activeVideoIndex].changeFadeClass('out');
                            }
                        }
                    }

                    return;
                }

                const indexToChange = this.activeVideoIndex === 0 ? 1 : 0;

                if (toPlay > fadeTimeInSeconds + 2) {
                    const activeVideos = [
                        ...this.activeVideos
                    ];
                    activeVideos[indexToChange] = {
                        ...nextVideo,
                        src: {
                            src: nextVideo.src
                        }
                    };

                    this.localAutoplay = false;

                    this.activeVideos = activeVideos;

                    if (this.$refs.videoPlayers[indexToChange]) {
                        this.$refs.videoPlayers[indexToChange].setCurrentTime(0);
                        this.$refs.videoPlayers[indexToChange].playPlayer();
                        this.$refs.videoPlayers[indexToChange].changeFadeClass('out');
                    }
                }

                if (toPlay <= Math.max(fadeTimeInSeconds, 1)) {
                    if (!this.isTransitioning) {
                        this.isTransitioning = true;

                        if (this.fadeTime && this.$refs.videoPlayers[this.activeVideoIndex]) {
                            this.$refs.videoPlayers[this.activeVideoIndex].changeFadeClass('out');
                        }

                        this.clearFadeInTransitionTimer();

                        if (this.fadeTime) {
                            this.fadeInTransitionTimerId = setTimeout(() => {
                                this.startNext(indexToChange);
                            }, this.fadeTime);
                        } else {
                            this.startNext(indexToChange);
                        }
                    }
                }
            },

            startNext(indexToChange) {
                if (this.$refs.videoPlayers[indexToChange]) {
                    this.$refs.videoPlayers[indexToChange].changeFadeClass('out');
                    this.$refs.videoPlayers[indexToChange].setCurrentTime(0);
                    this.$refs.videoPlayers[indexToChange].play(); // triggers fading
                }

                this.clearTransitionTimer();

                this.fadeInTransitionTimerId = setTimeout(() => {
                    this.isTransitioning = false;
                    this.localAutoplay = this.autoplay;
                }, this.fadeTime);
            },

            videoFinished(video) {
                if (video !== this.activeVideo) {
                    return;
                }

                if (this.$refs.videoPlayers[this.activeVideoIndex]) {
                    this.$refs.videoPlayers[this.activeVideoIndex].changeFadeClass('out');
                }
                this.activeVideoIndex = this.activeVideoIndex === 0 ? 1 : 0;

                this.playlistItem++;

                this.clearTransitionTimer();

                this.transitionTimerId = setTimeout(() => {
                    this.isTransitioning = false;
                }, this.fadeTime);
            },

            clearTransitionTimer() {
                if (this.transitionTimerId) {
                    clearTimeout(this.transitionTimerId);
                }
            },

            clearFadeInTransitionTimer() {
                if (this.fadeInTransitionTimerId) {
                    clearTimeout(this.transitionTimerId);
                }
            }
        }
    };
</script>

<style lang="scss" scoped>

    .video-player-playlist-item {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        opacity: 0;

        &.show-player {
            opacity: 1;
        }
    }

</style>
